import { useCallback } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useAppFlagStore } from '@frontend/shared';
import { GetAggregatedReviewsIO } from '../types';
import {
  UseReviewsAggregatorV1InfiniteQueryEndpointArgs,
  useReviewsAggregatorV1InfiniteQuery,
} from './use-reviews-aggregator-v1-infinite-query';

type Request<E = unknown, D = GetAggregatedReviewsIO['output']> = Parameters<
  typeof useReviewsAggregatorV1InfiniteQuery<'GetAggregatedReviews', E, D>
>[0]['request'];
type RequestFn<E = unknown, D = GetAggregatedReviewsIO['output']> = Exclude<
  Request<E, D>,
  GetAggregatedReviewsIO['input']
>;

/**
 * A hook that enforces access control list (ACL) permissions on review aggregator requests.
 *
 * This hook:
 * 1. Checks if ACL enforcement is enabled via the 'nwx:reviews-fe-acl-enforcement' feature flag
 * 2. When enabled, filters locationIds based on whether the user has required review permissions
 * 3. Returns a function that transforms the original request by applying these ACL-filtered locationIds
 *
 * @returns A function that takes a request object or function and returns a new request function
 * with ACL enforcement applied to the locationIds
 */
const useEnforceACLOnRequest = () => {
  const { getFeatureFlagValue } = useAppFlagStore();

  const enforcer = useCallback(
    <E = unknown, D = GetAggregatedReviewsIO['output']>(request: Request<E, D>): RequestFn<E, D> => {
      return (context) => {
        const originalRequest = typeof request === 'function' ? request(context) : request;
        const aclEnforcementEnabled = getFeatureFlagValue({
          flagName: 'nwx:reviews-fe-acl-enforcement',
          locationIds: originalRequest.locations ?? [],
          strategy: 'ALL',
        });
        const locationIdsWithACL =
          (aclEnforcementEnabled
            ? filterLocationIdsByACLs({
                locationIds: originalRequest.locations ?? [],
                acls: [Permission.REVIEW_ANALYTICS_READ, Permission.REVIEW_READ],
                strategy: 'any',
              })
            : originalRequest.locations) ?? [];

        return {
          ...originalRequest,
          locationIds: locationIdsWithACL,
        };
      };
    },
    [getFeatureFlagValue]
  );

  return enforcer;
};

type GetAggregatedReviewsPageParams = {
  pageKey?: string;
};
/**
 * A hook that returns an infinite query for the `GetAggregatedReviews` endpoint.
 * @param request The request object to pass to the query. Overriding pagination fields on the request is handled by the hook.
 * @param options (optional) The options to pass to `useInfiniteQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetAggregatedReviewsQuery = <E = unknown, D = GetAggregatedReviewsIO['output']>({
  options,
  request,
  ...args
}: UseReviewsAggregatorV1InfiniteQueryEndpointArgs<'GetAggregatedReviews', E, D, GetAggregatedReviewsPageParams>) => {
  const enforceACLOnRequest = useEnforceACLOnRequest();
  return useReviewsAggregatorV1InfiniteQuery<'GetAggregatedReviews', E, D, GetAggregatedReviewsPageParams>({
    endpointName: 'GetAggregatedReviews',
    ...args,
    request: ({ pageParam }) => {
      const requestWithPageParams: GetAggregatedReviewsIO['input'] = {
        ...request,
        pageKey: pageParam?.pageKey ?? request.pageKey,
      };
      return enforceACLOnRequest(requestWithPageParams)({});
    },
    options: {
      ...options,
      getNextPageParam: (lastPage) => {
        const nextPageKey = lastPage?.pagination?.nextPageKey;
        return nextPageKey ? { pageKey: nextPageKey } : undefined;
      },
      getPreviousPageParam: (firstPage) => {
        const prevPageKey = firstPage?.pagination?.prevPageKey;
        return prevPageKey ? { pageKey: prevPageKey } : undefined;
      },
    },
  });
};
